// src/pages/admin/Orders.jsx
import React, { useEffect, useState } from "react";
import { getOrders, getDesigners, createOrder, updateOrder, getSettings, defaultSettings, commissionRateForDesignerProfile } from "../../lib/db";
import { Btn, Badge, TableCard, Table, Modal, Field, Input, Select, Toast, Spinner, fmt$, fmtDate, orderStatusForBadge } from "../../components/UI";

const STATUS_OPTIONS = ["pending_approval","rejected","new","processing","delivered","paid","cancelled"];

const emptyForm = () => ({
  orderNumber:"", designerUid:"", designerName:"", clientName:"", projectId:"",
  description:"", amount:"", commissionRate: String(defaultSettings.tiers[0].rate), status:"new",
  attachmentUrl:"", attachmentName:"",
});

export default function Orders() {
  const pagePad = "var(--page-pad-x)";
  const [orders, setOrders]       = useState([]);
  const [designers, setDesigners] = useState([]);
  const [programSettings, setProgramSettings] = useState(defaultSettings);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [toast, setToast]         = useState("");
  const [search, setSearch]       = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState(emptyForm);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const selectedDesigner = designers.find((x) => x.id === form.designerUid);
  const profileRate = selectedDesigner ? commissionRateForDesignerProfile(selectedDesigner, programSettings) : null;
  const rateNum = Number(String(form.commissionRate).replace(/,/g, ""));
  const effectiveCommissionRate = Number.isFinite(rateNum) ? rateNum : profileRate;
  const commission = Math.round(
    (Number(form.amount) * (Number.isFinite(effectiveCommissionRate) ? effectiveCommissionRate : 0)) / 100
  );

  const load = async () => {
    setLoading(true);
    const [o, d, s] = await Promise.all([getOrders(), getDesigners(), getSettings()]);
    setOrders(o); setDesigners(d); setProgramSettings(s); setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openLogModal = () => {
    setForm(emptyForm());
    setModal(true);
  };

  const handleAdd = async () => {
    const salesOrder = form.orderNumber.trim();
    const pid = form.projectId.trim();
    if (!salesOrder) {
      setToast("Sales order number is required."); setTimeout(() => setToast(""), 3000);
      return;
    }
    if (!pid) {
      setToast("Project ID is required."); setTimeout(() => setToast(""), 3000);
      return;
    }
    if (!form.designerUid) {
      setToast("Please select a designer."); setTimeout(() => setToast(""), 3000);
      return;
    }
    const d = designers.find(x => x.id === form.designerUid);
    const resolvedRate = Number.isFinite(rateNum) ? rateNum : commissionRateForDesignerProfile(d, programSettings);
    if (resolvedRate == null || !Number.isFinite(Number(resolvedRate))) {
      setToast("Enter a commission % or choose a designer whose rate is finalized under Designers.");
      setSubmitting(false);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        orderNumber:    salesOrder,
        projectId:      pid,
        designerUid:    form.designerUid,
        designerName:   d?.name || "",
        clientName:     form.clientName,
        description:    form.description,
        amount:         Number(form.amount),
        commissionRate: resolvedRate,
        status:         form.status,
      };

      const orderId = await createOrder(payload);

      const link = form.attachmentUrl.trim();
      const linkLabel = form.attachmentName.trim();
      if (link) {
        await updateOrder(orderId, {
          attachmentUrl: link,
          attachmentName: linkLabel || "Attachment link",
        });
      }

      setModal(false);
      setForm(emptyForm());
      setToast("Order logged!"); setTimeout(() => setToast(""), 3000);
      load();
    } catch (err) {
      setToast(err.message || "Could not save order."); setTimeout(() => setToast(""), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (order, newStatus) => {
    await updateOrder(order.id, { status: newStatus });
    setToast(`Order updated to "${newStatus}"`); setTimeout(() => setToast(""), 3000);
    load();
  };

  const handleApproveOrder = async (order) => {
    await updateOrder(order.id, { status: "new" });
    setToast("Order approved."); setTimeout(() => setToast(""), 3000);
    load();
  };

  const handleRejectOrder = async (order) => {
    await updateOrder(order.id, { status: "rejected" });
    setToast("Order rejected."); setTimeout(() => setToast(""), 3000);
    load();
  };

  const filtered = orders.filter(o =>
    !search || `${o.orderNumber} ${o.designerName} ${o.clientName} ${o.projectId || ""}`.toLowerCase().includes(search.toLowerCase())
  );

  const cols = [
    { key:"num", label:"Sales Order #" },
    { key:"designer", label:"Designer" },
    { key:"projectId", label:"Project ID" },
    { key:"client", label:"Client / Project" },
    { key:"attach", label:"Attachment" },
    { key:"amountFmt", label:"Amount" },
    { key:"commFmt", label:"Commission" },
    { key:"statusEl", label:"Status" },
    { key:"dateFmt", label:"Date" },
    { key:"actions", label:"" },
  ];

  const rows = filtered.map(o => ({
    num: <span style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:400}}>{o.orderNumber || o.id.slice(0,8).toUpperCase()}</span>,
    designer: o.designerName || "—",
    projectId: o.projectId || "—",
    client: o.clientName,
    attach: o.attachmentUrl ? (
      <a href={o.attachmentUrl} target="_blank" rel="noopener noreferrer" style={{ color: "var(--gold)", fontWeight: 500 }}>
        {o.attachmentName || "Open file"}
      </a>
    ) : "—",
    amountFmt: fmt$(o.amount),
    commFmt: <span style={{color:"var(--gold)",fontWeight:400}}>{fmt$(o.commission)}</span>,
    statusEl: <Badge label={orderStatusForBadge(o.status)} />,
    dateFmt: fmtDate(o.createdAt),
    actions: o.status === "pending_approval" ? (
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <Btn size="sm" variant="gold" onClick={() => handleApproveOrder(o)}>Approve</Btn>
        <Btn size="sm" variant="danger" onClick={() => handleRejectOrder(o)}>Reject</Btn>
      </div>
    ) : (
      <Select value={o.status} onChange={e => handleStatusChange(o, e.target.value)}
        style={{ fontSize:11, padding:"5px 10px", width:130 }}>
        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
      </Select>
    ),
  }));

  return (
    <div>
      <Toast msg={toast} onClose={() => setToast("")} />
      <div style={{ padding:`28px ${pagePad} 0` }}>
        <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:36, fontWeight:300 }}>Orders</h1>
        <p style={{ fontSize:13, color:"var(--mid)", marginTop:4, maxWidth:900, lineHeight:1.55 }}>All designer-referred orders. Setting status to <strong>Delivered</strong> or <strong>Paid</strong> credits the designer&apos;s program-year totals once (YTD sales on their dashboard).</p>
      </div>
      <div style={{ padding:`var(--page-pad-y) ${pagePad} 32px` }}>
        <TableCard title={`All Orders (${filtered.length})`}
          actions={<>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…"
              style={{ padding:"8px 12px", border:"1px solid var(--border)", borderRadius:2, fontFamily:"'Jost',sans-serif", fontSize:13, fontWeight:300, background:"var(--cream)", outline:"none", width:200 }} />
            <Btn variant="gold" size="sm" onClick={openLogModal}>+ Log Order</Btn>
          </>}
        >
          {loading ? <Spinner /> : <Table cols={cols} rows={rows} emptyMsg="No orders yet." />}
        </TableCard>
      </div>

      <Modal open={modal} onClose={() => !submitting && setModal(false)} title="Log Order"
        footer={<>
          <Btn type="button" onClick={() => !submitting && setModal(false)} disabled={submitting}>Cancel</Btn>
          <Btn type="submit" form="log-order-form" variant="gold" disabled={submitting}>{submitting ? "Saving…" : "Log Order"}</Btn>
        </>}>
        <form id="log-order-form" onSubmit={e => { e.preventDefault(); handleAdd(); }}>
        <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
          <Field label="Sales Order Number" half required><Input name="orderNumber" value={form.orderNumber} onChange={set("orderNumber")} placeholder="DM-2026-050" required /></Field>
          <Field label="Designer" half required>
            <Select name="designerUid" value={form.designerUid} onChange={e => {
              const uid = e.target.value;
              const d = designers.find(x => x.id === uid);
              const rate = d ? commissionRateForDesignerProfile(d, programSettings) : null;
              setForm(f => ({ ...f, designerUid: uid, commissionRate: rate != null ? String(rate) : "" }));
            }} required>
              <option value="">— Select —</option>
              {designers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </Select>
          </Field>
        </div>
        <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
          <Field label="Project ID" half required><Input name="projectId" value={form.projectId} onChange={set("projectId")} placeholder="PRJ-2044-A" required /></Field>
          <Field label="Attachment link (optional)" half hint="External URL — no Firebase Storage on Spark.">
            <Input type="text" name="attachmentUrl" value={form.attachmentUrl} onChange={set("attachmentUrl")} placeholder="https://…" disabled={submitting} />
          </Field>
        </div>
        <Field label="Attachment link label (optional)"><Input value={form.attachmentName} onChange={set("attachmentName")} placeholder="Proposal PDF" disabled={submitting} /></Field>
        <Field label="Client / Project Name"><Input value={form.clientName} onChange={set("clientName")} placeholder="Smith Residence – Master Bath" /></Field>
        <Field label="Description"><Input value={form.description} onChange={set("description")} placeholder="Waterstone faucets, Newport tub filler…" /></Field>
        <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
          <Field label="Amount ($)" half hint="Exclude taxes and any shipping charges from this amount."><Input type="number" value={form.amount} onChange={set("amount")} placeholder="12400" /></Field>
          <Field label="Commission Rate (%)" half hint={profileRate == null && form.designerUid ? "Designer rate pending — enter a % manually or finalize them under Designers." : "Defaults from program tiers; edit for an admin override."}><Input type="text" inputMode="decimal" value={form.commissionRate} onChange={set("commissionRate")} placeholder={profileRate != null ? String(profileRate) : "e.g. 8"} /></Field>
        </div>
        <div style={{ background:"var(--gold-pale)", border:"1px solid rgba(184,150,90,0.3)", borderRadius:2, padding:"10px 14px", fontSize:13, color:"var(--gold)", marginBottom:14 }}>
          Commission: <strong>{Number.isFinite(effectiveCommissionRate) ? fmt$(commission) : "—"}</strong>
        </div>
        <Field label="Status">
          <Select value={form.status} onChange={set("status")}>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
        </Field>
        </form>
      </Modal>
    </div>
  );
}
